import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FlaskConical, UploadCloud, Loader2, FileText, Sparkles, ArrowLeft, Check,
  BookOpen, Layers3, Download, Printer, Brain, RotateCcw, Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { FlashcardPreview } from "@/components/FlashcardPreview";
import { analyzePdf, generatePlaygroundCards, pdfUrl } from "@/lib/api";

function PdfDropzone({ onFile, busy }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const handle = (files) => {
    const f = files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Bitte eine .pdf-Datei auswählen.");
      return;
    }
    onFile(f);
  };
  return (
    <div
      data-testid="pdf-dropzone"
      onClick={() => !busy && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-all ${
        drag ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30"
             : "border-slate-300 dark:border-slate-700 hover:border-violet-400 hover:bg-slate-50 dark:hover:bg-slate-800/40"
      }`}
    >
      <input ref={inputRef} data-testid="pdf-file-input" type="file" accept=".pdf" className="hidden" onChange={(e) => handle(e.target.files)} />
      <div className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-violet-600 text-white mb-4">
        {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <UploadCloud className="h-6 w-6" />}
      </div>
      <p className="font-display text-lg font-semibold text-slate-900 dark:text-slate-100">
        {busy ? "PDF wird analysiert…" : "Beliebiges PDF hier ablegen"}
      </p>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">oder klicken zum Auswählen</p>
      <span className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-300">
        <FileText className="h-3.5 w-3.5" /> PDF mit Text (kein Scan-Bild)
      </span>
    </div>
  );
}

export default function PlaygroundPage({ dark, toggleTheme }) {
  const [phase, setPhase] = useState("upload"); // upload | select | done
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null); // {source_id, filename, blocks}
  const [topic, setTopic] = useState(null);
  const [deck, setDeck] = useState(null);
  const [tab, setTab] = useState("summary"); // summary | cards
  const flip = "long";

  const reset = () => {
    setPhase("upload"); setAnalysis(null); setTopic(null); setDeck(null); setTab("summary");
  };

  const onFile = async (file) => {
    setBusy(true);
    try {
      const data = await analyzePdf(file);
      setAnalysis(data);
      setTopic(null);
      setPhase("select");
      toast.success(`${data.blocks.length} Themenblöcke erkannt.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyse fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const generate = async () => {
    if (!topic) { toast.error("Bitte einen Themenblock auswählen."); return; }
    setBusy(true);
    toast.loading("Karteikarten werden erzeugt…", { id: "gen" });
    try {
      const d = await generatePlaygroundCards(analysis.source_id, [topic]);
      setDeck(d);
      setPhase("done");
      toast.success(`${d.cards.length} Karteikarten erstellt.`, { id: "gen" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erzeugung fehlgeschlagen.", { id: "gen" });
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    toast.loading("PDF wird erzeugt…", { id: "pdf" });
    try {
      const res = await fetch(pdfUrl(deck.id, flip));
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "karteikarten-playground.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen.", { id: "pdf" });
    } catch {
      toast.error("PDF-Erzeugung fehlgeschlagen.", { id: "pdf" });
    }
  };

  return (
    <>
      <Header dark={dark} toggleTheme={toggleTheme} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {phase === "upload" && (
          <div className="max-w-2xl mx-auto">
            <div className="text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-violet-50 dark:bg-violet-950/40 px-3 py-1 text-xs font-semibold text-violet-700 dark:text-violet-300">
                <FlaskConical className="h-3.5 w-3.5" /> Playground
              </span>
              <h1 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
                Aus jedem PDF Lernkarten machen
              </h1>
              <p className="mt-2 text-slate-600 dark:text-slate-400">
                Lade ein beliebiges PDF hoch. Die KI findet die Themenblöcke, du wählst aus –
                und bekommst Karteikarten inkl. Lernsummary pro Thema.
              </p>
            </div>
            <div className="mt-8"><PdfDropzone onFile={onFile} busy={busy} /></div>
          </div>
        )}

        {phase === "select" && analysis && (
          <div className="max-w-3xl mx-auto">
            <button onClick={reset} className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100">
              <ArrowLeft className="h-4 w-4" /> Anderes PDF
            </button>
            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <FileText className="h-4 w-4" /> <span className="truncate">{analysis.filename}</span>
            </div>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              In dem Dokument sind folgende Themenblöcke drin
            </h1>
            <p className="mt-1 text-slate-600 dark:text-slate-400">Wozu soll ich dir Karteikarten machen? (Ein Thema auswählen)</p>

            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {analysis.blocks.map((b) => {
                const on = topic === b.title;
                const pages = b.page_start
                  ? `S. ${b.page_start}${b.page_end && b.page_end !== b.page_start ? "–" + b.page_end : ""}`
                  : null;
                return (
                  <button
                    key={b.title}
                    data-testid={`topic-block-${b.title}`}
                    onClick={() => setTopic(b.title)}
                    className={`text-left rounded-xl border p-4 transition-all ${
                      on ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30 ring-1 ring-violet-500"
                         : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-violet-300"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 grid place-items-center h-5 w-5 rounded-full shrink-0 ${on ? "bg-violet-600 text-white" : "border border-slate-300 dark:border-slate-600"}`}>
                        {on && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-slate-900 dark:text-slate-100">{b.title}</div>
                          {pages && (
                            <span className="shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-mono text-slate-500 dark:text-slate-400">
                              {pages}
                            </span>
                          )}
                        </div>
                        {b.description && <div className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{b.description}</div>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              data-testid="generate-cards-button"
              onClick={generate}
              disabled={busy || !topic}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
            >
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Wird erzeugt…</> : <><Sparkles className="h-4 w-4" /> Karteikarten erzeugen</>}
            </button>
            {busy && <p className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">Das kann einige Sekunden dauern…</p>}
          </div>
        )}

        {phase === "done" && deck && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <FileText className="h-4 w-4 shrink-0" /><span className="truncate">{deck.filename}</span>
                </div>
                <span data-testid="playground-cards-count" className="mt-1 inline-block text-lg font-display font-bold text-slate-900 dark:text-slate-100">
                  {deck.cards.length} Karteikarten · {deck.blocks.length} Lernsummary(s)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="playground-print-button" onClick={() => window.open(pdfUrl(deck.id, flip), "_blank")}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800">
                  <Printer className="h-4 w-4" /> Drucken
                </button>
                <button data-testid="playground-download-button" onClick={downloadPdf}
                  className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700">
                  <Download className="h-4 w-4" /> PDF
                </button>
                <button onClick={reset} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800">
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-700 p-1 bg-white dark:bg-slate-900 mb-6">
              <button data-testid="tab-summary" onClick={() => setTab("summary")}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "summary" ? "bg-violet-600 text-white" : "text-slate-600 dark:text-slate-300"}`}>
                <BookOpen className="h-4 w-4" /> Lernsummary
              </button>
              <button data-testid="tab-cards" onClick={() => setTab("cards")}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "cards" ? "bg-violet-600 text-white" : "text-slate-600 dark:text-slate-300"}`}>
                <Layers3 className="h-4 w-4" /> Karteikarten
              </button>
            </div>

            {tab === "summary" ? (
              <div className="space-y-4" data-testid="lernsummary-list">
                {deck.blocks.map((b, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                      <Lightbulb className="h-4 w-4" /> Lernsummary
                    </div>
                    <h3 className="mt-1 font-display text-xl font-bold text-slate-900 dark:text-slate-100">{b.title}</h3>
                    <p className="mt-2 text-slate-700 dark:text-slate-300 leading-relaxed">{b.summary}</p>
                    {b.schema?.length > 0 && (
                      <div className="mt-4 rounded-xl border border-violet-200 dark:border-violet-900 bg-violet-50/60 dark:bg-violet-950/20 p-4">
                        <div className="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                          Juristisches Prüfungsschema
                        </div>
                        <ol className="mt-2 space-y-1.5">
                          {b.schema.map((step, si) => (
                            <li key={si} className="flex gap-2 text-sm text-slate-700 dark:text-slate-300">
                              <span className="font-mono font-semibold text-violet-600 dark:text-violet-400 shrink-0">{si + 1}.</span>
                              <span>{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                ))}
                <Link to="/quiz" className="inline-flex items-center gap-2 rounded-xl bg-[#0F2942] dark:bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-[#1E3A5F] dark:hover:bg-blue-500">
                  <Brain className="h-4 w-4" /> Jetzt im Quiz üben
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {deck.cards.map((card) => <FlashcardPreview key={card.id} card={card} />)}
              </div>
            )}
          </div>
        )}
      </main>
    </>
  );
}
